import { useId, useState } from 'react'
import type { OperatingCostKind, OperatingCostValueType } from '@/types/api'
import type { CreateOperatingCostCategoryInput } from '@/lib/api/operating-costs-admin.service'
import styles from '../CustosOperacionaisPage.module.css'

type CreateCategoryModalProps = {
  isOpen: boolean
  isSaving: boolean
  kind: OperatingCostKind
  error: string | null
  onClose: () => void
  onSubmit: (input: CreateOperatingCostCategoryInput) => Promise<boolean>
}

export function CreateCategoryModal({
  isOpen,
  isSaving,
  kind,
  error,
  onClose,
  onSubmit,
}: CreateCategoryModalProps) {
  const titleId = useId()
  const [name, setName] = useState('')
  const [valueType, setValueType] = useState<OperatingCostValueType>('amount')
  const [localError, setLocalError] = useState<string | null>(null)

  if (!isOpen) return null

  const canSubmit = name.trim() !== '' && !isSaving
  const title = kind === 'fixed' ? 'Nova categoria fixa' : 'Nova categoria variável'

  return (
    <div
      className={styles.overlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSaving) {
          onClose()
        }
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className={styles.dialogHeader}>
          <h2 id={titleId} className={styles.dialogTitle}>
            {title}
          </h2>
          <button
            type="button"
            className={styles.iconButton}
            onClick={onClose}
            disabled={isSaving}
            aria-label="Fechar"
          >
            Fechar
          </button>
        </header>
        <form
          className={styles.dialogBody}
          onSubmit={(event) => {
            event.preventDefault()
            const trimmed = name.trim()
            if (trimmed === '') {
              setLocalError('Informe o nome da categoria.')
              return
            }
            setLocalError(null)
            void onSubmit({ name: trimmed, kind, valueType }).then((ok) => {
              if (ok) {
                setName('')
                setValueType('amount')
                onClose()
              }
            })
          }}
        >
          <label className={styles.field} htmlFor="operating-cost-category-name">
            Nome
            <input
              id="operating-cost-category-name"
              className={styles.input}
              value={name}
              maxLength={80}
              onChange={(event) => setName(event.target.value)}
              placeholder="Ex.: Internet do escritório"
              disabled={isSaving}
            />
          </label>

          <fieldset className={styles.fieldset}>
            <legend>Forma de valor</legend>
            <label className={styles.radio}>
              <input
                type="radio"
                name="operating-cost-value-type"
                checked={valueType === 'amount'}
                onChange={() => setValueType('amount')}
                disabled={isSaving}
              />
              Reais (R$)
            </label>
            <label className={styles.radio}>
              <input
                type="radio"
                name="operating-cost-value-type"
                checked={valueType === 'percent'}
                onChange={() => setValueType('percent')}
                disabled={isSaving}
              />
              Percentual (%)
            </label>
          </fieldset>

          {localError || error ? (
            <p className={styles.inlineError} role="alert">
              {localError ?? error}
            </p>
          ) : null}

          <div className={styles.dialogActions}>
            <button type="button" className={styles.secondaryButton} onClick={onClose} disabled={isSaving}>
              Cancelar
            </button>
            <button type="submit" className={styles.primaryButton} disabled={!canSubmit}>
              {isSaving ? 'Salvando...' : 'Criar categoria'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
